/**
 * api-fetch/trello-teams — verbatim KanbanApi method block extracted from the
 * former api.fetch.ts monolith. Closes over the shared {@link ApiFetchContext};
 * behaviour is unchanged. Composed by spread in createKanbanFetchApi.
 */
import type {
  ActionExecution,
  ActionTemplate,
  Allocation,
  Board,
  BoardTemplate,
  BoardTemplateAuditLogPage,
  BoardTemplateIntegrityReport,
  Card,
  CardAiThread,
  CardAttachment,
  CardChecklistItemT,
  CardChecklistT,
  CardCommentT,
  CardDependency,
  CostIntelligenceSummary,
  QuoteStageSummaryRow,
  QuoteConvertOptions,
  QuoteConvertResult,
  TrelloCardDetail,
  CardScheduleContext,
  ClassificationRule,
  EnsureTemplateLaneIdsResult,
  Lane,
  BoardMembership,
  LaneMembership,
  LinkBoardToTemplateInput,
  LinkBoardToTemplateResult,
  ListAuditLogOptions,
  ListBoardTemplatesOptions,
  ListWorkflowPresetsOptions,
  BoardRelatedCounts,
  Notebook,
  NotebookActionItemT,
  NotebookListItem,
  NotebookRecordingT,
  OrgClassificationMemory,
  ReconcileBoardInput,
  ReconcileResult,
  RelatedCards,
  Tag,
  UnlinkBoardFromTemplateInput,
  WorkflowPreset,
} from '../types'

import type {
  ActionInvokeInput,
  ActionTemplateFilter,
  AllocateEmailToLaneInput,
  AllocateEmailToLaneResult,
  AllocationExplain,
  AllocationCreate,
  AllocationFilter,
  BoardCreate,
  BoardDefaultCategory,
  BoardListFilter,
  BoardSnapshot,
  BoardSummary,
  CardCreate,
  CardParseActionsResult,
  CardDependencyCreate,
  CardDependencyFilter,
  CardFilter,
  CardSearchResult,
  DueSoonFilter,
  LinkEmailToCardInput,
  LinkEmailToCardResult,
  KanbanCalendarActionExtract,
  AssignedToMeFilter,
  CardMoveInput,
  CardTeamMessage,
  CardTeamMessageListResponse,
  CardTeamsThread,
  KanbanAllocationConflict,
  KanbanApi,
  KanbanBoardVersion,
  KanbanCasOpts,
  KanbanSearchResponse,
  LaneCardCount,
  LaneCreate,
  LaneListOptions,
  InstallWorkflowPresetInput,
  InstallWorkflowPresetResult,
  PromoteLaneToBoardResult,
  SeedDefaultBoardsInput,
  SeedDefaultBoardsResult,
  TagCreate,
  TeamMemberAddInput,
  TeamMemberAddResult,
  TeamRosterSyncResult,
  TrelloCardWritebackPatch,
  TrelloCardWritebackResult,
  TrelloSyncStatusResponse,
  TrelloConnectionSettings,
  TrelloConnectionSettingsPatch,
  TrelloCardDriftList,
  TrelloCardDriftResolveResult,
  SyncDriftResolution,
} from '../api'
import { KanbanCardConflictError, KanbanNetworkError } from '../api'
import type { TrelloEmailLinkDecisionResult } from '../types'
import type {
  BoardMeeting,
  MeetingAnalyzeResult,
  MeetingLivePayload,
  MeetingSegmentIngestResult,
  MeetingSuggestionDecision,
} from '../meetingTypes'
import type { ApiFetchContext } from './context'

export function createTrelloTeamsApi(ctx: ApiFetchContext) {
  const { _request, _trelloRequest, _casHeaders, getAuthHeaders, trelloApiBase, teamsApiBase } = ctx
  return {
    // FEAT-2026-05-21-TRELLO-VIS — Stage 5b
    // -------------------------------------
    // Backend-authoritative sync state for the caller's own org.
    // Goes through `_request` because the endpoint lives under
    // the kanban surface (`/api/kanban/trello-sync-status/`), so
    // auth headers, baseUrl, error normalisation, and the same
    // 401 KanbanAuthRequiredError treatment apply uniformly with
    // every other call.
    //
    // Returned shape is `TrelloSyncStatusResponse` (see api.ts);
    // the rail's pill uses `connections[*].freshness` +
    // `last_delta_age_seconds` to render the colour + label.
    // The client-side estimator (`computeTrelloSyncStatus` in
    // trello-sync-status.ts) is kept as the fallback when this
    // call fails so old deployments + transient 5xx don't kill
    // the pill silently.
    fetchTrelloSyncStatus: () =>
      _request<TrelloSyncStatusResponse>(
        'GET', '/trello-sync-status/',
      ),

    // FEAT-2026-06-24-SYNC-DIRECTION — sync-direction settings +
    // card-drift review. All live under `/api/trello/` (the mirror
    // surface), so they go through `_trelloRequest`.
    getTrelloConnectionSettings: () =>
      _trelloRequest<TrelloConnectionSettings>('GET', '/connection/settings/'),
    updateTrelloConnectionSettings: (patch: TrelloConnectionSettingsPatch) =>
      _trelloRequest<TrelloConnectionSettings>('PATCH', '/connection/settings/', patch),
    listTrelloCardDrifts: () =>
      _trelloRequest<TrelloCardDriftList>('GET', '/card-drifts/'),
    resolveTrelloCardDrift: (driftId: number | string, resolution: SyncDriftResolution) =>
      _trelloRequest<TrelloCardDriftResolveResult>(
        'POST', `/card-drifts/${encodeURIComponent(String(driftId))}/resolve/`,
        { resolution },
      ),

    // Trello mirror — rich detail. Direct fetch (not via `_request`)
    // because the URL prefix differs (`/api/trello/` vs the kanban
    // `baseUrl`). Auth headers + credentials are reused so the
    // request behaves the same as every other Kanban call.
    fetchTrelloCardDetail: async (externalId: string) => {
      const headers: Record<string, string> = {
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = trelloApiBase.replace(/\/+$/, '')
      const url = `${cleanBase}/cards/${encodeURIComponent(externalId)}/`
      const res = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `GET ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
      return (await res.json()) as TrelloCardDetail
    },

    // Teams Hub bridge (qb!2758). The bridge endpoint sits on the
    // kanban surface (`/api/kanban/cards/<id>/teams-thread/`); the
    // live message list + send go directly to the existing Teams
    // REST surface (`/api/teams/channels/<channel_id>/...`).
    fetchCardTeamsThread: (cardId: string) =>
      _request<CardTeamsThread>('GET', `/cards/${cardId}/teams-thread/`),

    listCardTeamMessages: async (
      channelId: string,
      opts?: { before?: string; limit?: number },
    ) => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = teamsApiBase.replace(/\/+$/, '')
      const usp = new URLSearchParams()
      if (opts?.before) usp.set('before', opts.before)
      if (opts?.limit !== undefined) usp.set('limit', String(opts.limit))
      const qs = usp.toString()
      const url =
        `${cleanBase}/channels/${encodeURIComponent(channelId)}/messages/` +
        (qs ? `?${qs}` : '')
      const res = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `GET ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
          else if (j?.error) err = j.error
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
      return (await res.json()) as CardTeamMessageListResponse
    },

    sendCardTeamMessage: async (
      channelId: string,
      payload: {
        content: string
        mentions?: number[]
        reply_to?: string
        attachment_ids?: string[]
      },
    ) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = teamsApiBase.replace(/\/+$/, '')
      const url = `${cleanBase}/channels/${encodeURIComponent(channelId)}/messages/`
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `POST ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
          else if (j?.error) err = j.error
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
      return (await res.json()) as CardTeamMessage
    },

    // Delete a single Teams message (sender-only; the backend
    // `TeamMessageDetailAPI.delete` soft-deletes and 403s a non-sender).
    // Raw `fetch` (NOT the `_request`/quotechFetch helper) so a routine
    // 401 here can only surface as a caught mutation error — it can never
    // fire the auth-lost bounce that kicks the whole tab to /login. Same
    // transport rationale as send/list above. (BUG-2026-06-29 comment
    // delete + post-refresh.)
    deleteCardTeamMessage: async (channelId: string, messageId: string) => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = teamsApiBase.replace(/\/+$/, '')
      const url =
        `${cleanBase}/channels/${encodeURIComponent(channelId)}` +
        `/messages/${encodeURIComponent(messageId)}/`
      const res = await fetch(url, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      })
      // 204 No Content on success; tolerate any 2xx.
      if (!res.ok) {
        let err = `DELETE ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
          else if (j?.error) err = j.error
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
    },

    // Edit a single Teams message body (sender-only + 15-min window,
    // enforced by `TeamMessageDetailAPI.patch`). Raw `fetch` (NOT the
    // `_request`/quotechFetch helper) for the SAME reason as send/delete:
    // a routine 401 here surfaces as a caught mutation error and can never
    // fire the auth-lost bounce that kicks the whole tab to /login. The BE
    // returns only `{id, content, is_edited}`, so the caller patches its
    // cached message optimistically rather than replacing the envelope.
    editCardTeamMessage: async (
      channelId: string,
      messageId: string,
      content: string,
    ) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = teamsApiBase.replace(/\/+$/, '')
      const url =
        `${cleanBase}/channels/${encodeURIComponent(channelId)}` +
        `/messages/${encodeURIComponent(messageId)}/`
      const res = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ content }),
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `PATCH ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
          else if (j?.error) err = j.error
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
      return (await res.json()) as {
        id: string
        content: string
        is_edited: boolean
      }
    },

  }
}
